import type { CityConfig } from "../city-template/types";

export const fontanaConfig: CityConfig = {
  vitalRecordsIcon: "ri-file-text-line",
  city: "Fontana",
  state: "California",
  stateAbbr: "CA",
  slug: "fontana",
  route: "/fontana",
  title: "Emergency Passport Fontana, CA — Expedited & Fast Passport Service | Fast Passport Center",
  metaDescription: "Emergency passport and expedited passport services in Fontana, CA — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for Inland Empire travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Fontana, CA",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport fast? We help Fontana and San Bernardino County travelers get their expedited passports as quickly as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Fontana%20California%20panoramic%20view%20at%20golden%20hour%20with%20San%20Gabriel%20Mountains%20in%20the%20background%2C%20modern%20suburban%20neighborhoods%20and%20Inland%20Empire%20landscape%2C%20warm%20sunset%20light%20over%20the%20valley%2C%20professional%20architectural%20photography%2C%20wide%20panoramic%20view%20of%20Southern%20California%20foothills%2C%20no%20visible%20text%20or%20people%2C%20serene%20suburban%20environment%20with%20warm%20amber%20and%20orange%20tones&width=1800&height=700&seq=fontana-hero-001&orientation=landscape",
  heroImageAlt: "Fontana California panoramic view at golden hour with San Gabriel Mountains and suburban neighborhoods",
  geoPosition: "34.0922;-117.4350",
  geoPlacename: "Fontana, California",
  lastModified: "2026-06-14",
  regionalAgency: {
    name: "Los Angeles Passport Agency",
    address: "11000 Wilshire Blvd Suite 1000, Los Angeles, CA 90024",
    distance: "Located in West LA — approximately 60–75 minutes from Fontana via I-10",
  },
  nearbyCitiesZips: [
    { name: "Fontana", zips: "92331, 92335, 92336, 92337" },
    { name: "Rancho Cucamonga", zips: "91701, 91730, 91739" },
    { name: "Rialto", zips: "92376, 92377" },
    { name: "San Bernardino", zips: "92401–92411" },
    { name: "Riverside", zips: "92501–92509" },
    { name: "Ontario", zips: "91761, 91762, 91764" },
    { name: "Colton", zips: "92324" },
    { name: "Bloomington", zips: "92316" },
    { name: "Jurupa Valley", zips: "91752, 92509" },
    { name: "Redlands", zips: "92373, 92374" },
    { name: "Chino", zips: "91708, 91710" },
    { name: "Upland", zips: "91784, 91786" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent Inland Empire departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels to our processing center" },
  ],
  internalLinks: [
    { name: "Los Angeles", route: "/los-angeles" },
    { name: "Rancho Cucamonga", route: "/rancho-cucamonga" },
    { name: "San Bernardino", route: "/san-bernardino" },
    { name: "Rialto", route: "/rialto" },
    { name: "Ontario", route: "/ontario" },
    { name: "Riverside", route: "/riverside" },
    { name: "Anaheim", route: "/anaheim" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Fontana remotely via secure courier. We do not have a walk-in office in Fontana — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Inland Empire travelers.",
  fpcOfficeAddress: "355 S Grand Ave, Ste 2450, Los Angeles, CA 90071",
  fpcOfficeDistance: "Serving all of San Bernardino County",
  serviceAreaIntro: "From the Southridge area to Northgate, from downtown Fontana to the Sierra Lakes community — Fast Passport Center serves the entire Fontana area. We regularly help travelers from Rancho Cucamonga, Rialto, San Bernardino, and Ontario get their passports fast.",
  nearbyCitiesIntro: "We serve all Fontana neighborhoods and nearby Inland Empire cities including Rancho Cucamonga, Rialto, San Bernardino, Ontario, Colton, and Bloomington. If you are in San Bernardino County, we have you covered.",
  howItWorksIntro: "Most Fontana travelers complete everything online and ship documents overnight. No need to drive to Los Angeles or wait in line at the regional agency.",
  mistakesIntro: "With Ontario International Airport (ONT) serving as the Inland Empire's primary gateway, Fontana travelers can't afford passport delays. Here are the most common mistakes we see.",
  testimonialsIntro: "Fontana and Inland Empire travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Fontana? Here are the most common ones we hear from San Bernardino County travelers.",
  statePageRoute: "/states/california",
  statePageName: "California",
  dmvWebsite: "https://www.dmv.ca.gov",
  dmvLabel: "California DMV — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Predictable turnaround", "Ideal for upcoming trips", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Faster than self-service", "Good for planned travel", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: ["No courier access — standard mail only", "Workload & wait times shift with shutdowns and budget fights", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent — you are on your own", "Updates only by physical mail letter", "No phone or email contact for your case", "Can't upgrade speed after submission", "Not suitable if travel is within 10 weeks"],
      highlight: false,
      muted: true,
    },
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: ["Appointment required — no walk-ins allowed", "Life-or-death emergencies only", "Bereavement or medical crisis", "Expect to wait all day or return next day"],
      highlight: false,
      emergency: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For travelers with immediate departure needs — as fast as 24 hours for urgent Inland Empire international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Michael R.",
      location: "Fontana, CA",
      rating: 5,
      text: "My flight to Mexico City was in 3 days and my passport was expired. Fast Passport Center got me a new one in under 48 hours. Absolutely saved my business trip.",
      service: "Emergency Passport",
    },
    {
      name: "Vanessa G.",
      location: "Rancho Cucamonga, CA",
      rating: 5,
      text: "Needed to renew for a family trip to the Philippines. The process was incredibly smooth, they reviewed my documents, and I had my passport in 4 days. Highly recommend for Inland Empire families.",
      service: "Passport Renewal",
    },
    {
      name: "David S.",
      location: "Rialto, CA",
      rating: 5,
      text: "Lost my passport a week before my honeymoon to Cancun. They walked me through the DS-64 process and got me a replacement in 36 hours. Incredible service.",
      service: "Lost Passport",
    },
    {
      name: "Jennifer L.",
      location: "San Bernardino, CA",
      rating: 5,
      text: "As a frequent international traveler from the Inland Empire, I can't afford passport delays. Fast Passport Center has been reliable every time. Highly recommend for all San Bernardino County residents.",
      service: "Expedited Renewal",
    },
  ],
  facilities: [
    {
      id: "font-1",
      name: "Fontana Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "10200 Sierra Ave",
      city: "Fontana",
      state: "CA",
      zip: "92335",
      phone: "(909) 822-4467",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.4 miles from downtown Fontana",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Acceptance hours are limited. Appointments required. Call ahead to confirm passport acceptance times.",
      icon: "ri-mail-line",
    },
    {
      id: "font-2",
      name: "Rancho Cucamonga Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "9677 Base Line Rd",
      city: "Rancho Cucamonga",
      state: "CA",
      zip: "91730",
      phone: "(909) 481-3544",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "4.5 miles from Fontana",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Major facility for western San Bernardino County. Appointment strongly recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "font-3",
      name: "San Bernardino Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "390 W 5th St",
      city: "San Bernardino",
      state: "CA",
      zip: "92401",
      phone: "(909) 884-4514",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "8 miles from Fontana",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Largest facility in the region. Book appointments 2+ weeks in advance. Photo services available.",
      icon: "ri-mail-line",
    },
    {
      id: "font-4",
      name: "Ontario Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "224 N Euclid Ave",
      city: "Ontario",
      state: "CA",
      zip: "91762",
      phone: "(909) 983-1462",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "6 miles from Fontana",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Popular for Ontario and Fontana residents near the 10 freeway. Appointment required.",
      icon: "ri-mail-line",
    },
    {
      id: "font-5",
      name: "Rialto Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "200 N Willow Ave",
      city: "Rialto",
      state: "CA",
      zip: "92376",
      phone: "(909) 875-0415",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "3 miles from Fontana",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for eastern Fontana area residents. Limited passport hours — call to confirm.",
      icon: "ri-mail-line",
    },
    {
      id: "font-6",
      name: "San Bernardino County Assessor-Recorder",
      type: "County Clerk Passport Acceptance",
      address: "222 W Hospitality Ln",
      city: "San Bernardino",
      state: "CA",
      zip: "92415",
      phone: "(855) 732-2575",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "7 miles from Fontana",
      website: "https://www.sbcounty.gov/arc/",
      websiteLabel: "SB County Assessor-Recorder",
      notes: "Major county facility with extended hours. Accepts walk-ins but appointments recommended for faster service. Photo services on-site.",
      icon: "ri-government-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — West LA (60–75 min drive)", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
    { feature: "Inland Empire Coverage", agency: "No IE regional expertise", fpc: "Fontana, Rancho Cucamonga, Ontario, and San Bernardino County travel expertise" },
    { feature: "Multi-Person Processing", agency: "Separate appointments per person", fpc: "Handle families and groups in parallel" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Inland Empire photo shops often use generic sizes. Passport photos must be exactly 2×2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at San Bernardino County acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many Fontana applicants show up with incorrect checks and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the Los Angeles regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many Inland Empire families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "San Bernardino County travelers often assume standard processing will be fine. It takes 8–11 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Fontana." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Fontana address — trackable every step of the way." },
  ],
  faqs: [
    { q: "Can I get an emergency passport in Fontana before international travel?", a: "Yes. Fast Passport Center provides emergency passport services in Fontana for travelers with urgent international flights. Our registered couriers achieve 24–48 hour turnaround for most Inland Empire travelers." },
    { q: "How fast can I renew my passport in Fontana?", a: "Fast Passport Center offers fast passport renewal in Fontana in as little as 24–48 hours through our emergency courier service. For less urgent travel, our 3–5 day expedited option provides the best value." },
    { q: "What if my passport expires before my cruise?", a: "If your passport expires before your cruise, contact Fast Passport Center immediately. We can expedite your passport renewal in Fontana in 24–48 hours so you don't miss your departure." },
    { q: "Can I get a same-day passport appointment near Fontana?", a: "Same-day passport appointments are only available at the Los Angeles Regional Passport Agency for documented life-or-death emergencies with proof of travel within 72 hours. For all other urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option for Fontana residents." },
    { q: "Can I expedite a child passport in Fontana?", a: "Yes. Fast Passport Center expedites child passports in Fontana for minors under 16. Both parents must appear or submit notarized consent, along with the child's birth certificate and both parents' IDs." },
    { q: "What should I do if I lost my passport before a trip?", a: "If you lost your passport before a trip, contact Fast Passport Center immediately. We provide lost passport replacement services in Fontana with DS-64 form guidance. Our emergency courier service can get your replacement passport in 24–48 hours." },
    {
      q: "How fast can I get a passport in Fontana?",
      a: "With Fast Passport Center's emergency courier service, Fontana travelers can receive their passport in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities, bypassing standard mail delays.",
    },
    {
      q: "Do I need to drive to the Los Angeles Passport Agency in West LA?",
      a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities. You only need to visit a local acceptance clerk if you're a first-time applicant or need a name change.",
    },
    {
      q: "Where are the closest passport acceptance facilities in Fontana?",
      a: "Popular options include the Fontana Post Office (92335), Rancho Cucamonga Post Office (91730), Ontario Post Office (91762), and San Bernardino County Assessor-Recorder (92415). All require appointments. We help you find the most convenient location and schedule your visit.",
    },
    {
      q: "Can I get a same-day passport in Fontana?",
      a: "Same-day passports are only available at the Los Angeles Regional Passport Agency for documented life-or-death emergencies with proof of travel within 72 hours. For all other urgent travel, Fast Passport Center's 24–48 hour emergency processing is the fastest reliable option.",
    },
    {
      q: "What if I live in Rancho Cucamonga, Rialto, or Ontario?",
      a: "We serve all of San Bernardino County including Rancho Cucamonga, Rialto, Ontario, San Bernardino, Colton, and Bloomington. Our overnight shipping labels work from any UPS location, so your location within the Inland Empire doesn't affect processing speed.",
    },
    {
      q: "How much does expedited passport service cost in Fontana?",
      a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed — emergency 24–48 hour processing, 3–5 day expedited, or standard options. Contact us for a personalized quote based on your timeline.",
    },
    {
      q: "Do you help with passport photos in Fontana?",
      a: "We guide you on exact photo requirements and can recommend locations near you. Many CVS, Walgreens, and FedEx locations in Fontana, Rancho Cucamonga, and Ontario offer passport photo services. We review your photos before submission to ensure they meet State Department standards.",
    },
    {
      q: "What documents do I need for a first-time passport in Fontana?",
      a: "You'll need proof of citizenship (birth certificate or naturalization certificate), government-issued photo ID (driver's license or REAL ID), two passport photos, and payment. We provide a complete checklist and review your documents before you visit the acceptance clerk.",
    },
    {
      q: "Can you help with a child's passport in Fontana?",
      a: "Yes. Child passports require both parents (or notarized consent from the absent parent), the child's birth certificate, and both parents' IDs. We walk San Bernardino County families through the entire process and help schedule acceptance clerk appointments.",
    },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Fontana", address: "16033 Sierra Lakes Pkwy, Fontana, CA 92336", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Rancho Cucamonga", address: "9600 Base Line Rd, Rancho Cucamonga, CA 91730", hours: "Mon–Sun: 7AM–11PM" },
    { name: "FedEx Office — Ontario", address: "4360 Mills Cir, Ontario, CA 91764", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM" },
    { name: "UPS Store — Fontana", address: "17098 Slover Ave, Fontana, CA 92337", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM" },
    { name: "Walmart — Fontana", address: "17251 Foothill Blvd, Fontana, CA 92335", hours: "Mon–Sun: 6AM–11PM" },
    { name: "Rite Aid — Rialto", address: "499 W Base Line Rd, Rialto, CA 92376", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Postal Annex — Rancho Cucamonga", address: "11235 Sierra Ave, Fontana, CA 92337", hours: "Mon–Fri: 9AM–6PM, Sat: 9AM–4PM" },
    { name: "Costco — Ontario", address: "4300 E Jurupa St, Ontario, CA 91761", hours: "Mon–Fri: 10AM–8:30PM, Sat–Sun: 9:30AM–6PM" },
  ],
  dmvLocations: [
    {
      name: "DMV — Fontana",
      address: "8026 Hemlock Ave, Fontana, CA 92336",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID and driver's license renewals. Appointment required.",
      featured: true,
    },
    {
      name: "DMV — Rancho Cucamonga",
      address: "8629 Hellman Ave, Rancho Cucamonga, CA 91730",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Passport-ready photo services available. Online appointment booking.",
      featured: false,
    },
    {
      name: "DMV — San Bernardino",
      address: "1310 N Waterman Ave, San Bernardino, CA 92404",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID processing. Walk-ins limited — book online first.",
      featured: false,
    },
    {
      name: "DMV — Riverside",
      address: "6425 Market St, Riverside, CA 92506",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Full-service center for Inland Empire residents.",
      featured: true,
    },
  ],
  airports: [
    {
      code: "ONT",
      name: "Ontario International Airport",
      distance: "10 miles from downtown Fontana",
      address: "1923 E Avion St, Ontario, CA 91761",
      note: "The primary airport for the Inland Empire. International flights to Mexico, Canada, and connections to Asia.",
      flightUrl: "https://www.flyontario.com/",
      flightLabel: "Ontario Airport",
      featured: true,
    },
    {
      code: "LAX",
      name: "Los Angeles International Airport",
      distance: "65 miles from Fontana",
      address: "1 World Way, Los Angeles, CA 90045",
      note: "Major international hub for long-haul flights to Asia, Europe, and worldwide destinations. Accessible via I-10.",
      flightUrl: "https://www.flylax.com/",
      flightLabel: "LAX Flight Info",
      featured: false,
    },
    {
      code: "SNA",
      name: "John Wayne Airport (Orange County)",
      distance: "45 miles from Fontana",
      address: "18601 Airport Way, Santa Ana, CA 92707",
      note: "Convenient for Orange County and western Inland Empire travelers. International flights to Mexico and Canada.",
      flightUrl: "https://www.ocair.com/",
      flightLabel: "John Wayne Airport",
      featured: false,
    },
  ],
  vitalRecordsLinks: [
    {
      icon: "ri-user-smile-line",
      label: "Birth Certificate",
      desc: "Request certified birth certificates from the California Department of Public Health Vital Records. Online, mail, or in-person options available for San Bernardino County residents. Processing typically takes 5–10 business days.",
      url: "https://www.cdph.ca.gov/Programs/CHSI/Pages/Vital-Records.aspx",
    },
    {
      icon: "ri-file-list-2-line",
      label: "Death Certificate",
      desc: "Obtain certified death certificates through California CDPH Vital Records. Required for estate and legal matters in San Bernardino County.",
      url: "https://www.cdph.ca.gov/Programs/CHSI/Pages/Vital-Records.aspx",
    },
    {
      icon: "ri-hearts-line",
      label: "Marriage Certificate",
      desc: "Request marriage license records from the San Bernardino County Assessor-Recorder. Certified copies available for name changes and legal purposes in Fontana and throughout the Inland Empire.",
      url: "https://www.sbcounty.gov/arc/",
    },
    {
      icon: "ri-file-copy-line",
      label: "Divorce Decree",
      desc: "Obtain certified divorce records through the San Bernardino County Superior Court. Required for name change passport corrections and legal documentation.",
      url: "https://www.sb-court.org/",
    },
  ],
};